import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import Select from "react-select";
import { Link, useLocation } from "react-router-dom";
import GoodsReturnForm from "./Goodsreturnform";
import { FileText } from 'lucide-react';
import { authHeaders } from "../../utils/authHeaders";

// ── Rounds a numeric value UP to at most 2 decimal places for clean display
const formatQty = (value) => {
    if (value === null || value === undefined || value === "") return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return Math.ceil(num * 100) / 100;
};

// ── Sums issued_qty across an array of stock rows for overall balances
const sumQty = (stocks) =>
    (stocks || []).reduce((sum, s) => sum + Number(s.issued_qty || s.qty || 0), 0);

// ── Converts an ISO date (yyyy-mm-dd) to display format (dd-mm-yyyy)
const isoToDMY = (iso) => (iso && dayjs(iso, "YYYY-MM-DD", true).isValid())
    ? dayjs(iso).format("DD-MM-YYYY")
    : "";

// ── Converts a dd-mm-yyyy display string back to ISO (yyyy-mm-dd)
const dmyToIso = (dmy) => {
    const d = dayjs(dmy, "DD-MM-YYYY", true);
    return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

// ── Auto-inserts dashes as the user types digits: d d - m m - y y y y
const maskDMYInput = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let out = digits.slice(0, 2);
    if (digits.length >= 3) out += "-" + digits.slice(2, 4);
    if (digits.length >= 5) out += "-" + digits.slice(4, 8);
    return out;
};

// ── Date filter input: always shows/accepts dd-mm-yyyy, independent of the
// browser/OS locale. Value/onChange are in ISO (yyyy-mm-dd) so the rest of
// the app (API calls, comparisons) is untouched. A native date input is
// tucked behind the calendar icon for quick picking via a real calendar UI.
const DateInputDMY = ({ value, onChange, style }) => {
    const [text, setText] = useState(isoToDMY(value));

    useEffect(() => {
        setText(isoToDMY(value));
    }, [value]);

    return (
        <div style={{ position: "relative", ...style }}>
            <input
                type="text"
                className="form-control"
                style={{ height: "38px", paddingRight: 34, width: "100%" }}
                placeholder="dd-mm-yyyy"
                maxLength={10}
                value={text}
                onChange={(e) => {
                    const masked = maskDMYInput(e.target.value);
                    setText(masked);
                    if (masked.length === 10) {
                        const iso = dmyToIso(masked);
                        if (iso) onChange(iso);
                    } else if (masked.length === 0) {
                        onChange("");
                    }
                }}
                onBlur={() => {
                    // Revert to last valid value if left incomplete/invalid
                    if (text.length !== 10 || !dmyToIso(text)) {
                        setText(isoToDMY(value));
                    }
                }}
            />
            <input
                type="date"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                title="Pick a date"
                style={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 20,
                    height: 20,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    color: "transparent",
                    colorScheme: "light"
                }}
            />
        </div>
    );
};

// ── Returns the logged-in user's ID (never name/email) — same robust
//    fallback chain used in GoodsRequest.js's getCurrentUserId, so this
//    works regardless of exactly which key the login response uses.
const getCurrentUserId = () => {
    const userObj = localStorage.getItem("user");
    if (userObj) {
        try {
            const parsedUser = JSON.parse(userObj);
            const id = parsedUser.userId ?? parsedUser.user_id ?? parsedUser.id;
            if (id !== undefined && id !== null && id !== "") return id;
        } catch (e) {
            console.error("Error parsing user data:", e);
        }
    }
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
    return "";
};

const USER_MANUAL_SCOPE = {
    moduleName: "Production",
    process: "Shop Floor Control",
    subProcess: "Goods Return"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

function GoodsReturn() {
    const location = useLocation();
    const [data, setData] = useState([]);
    const [returnedData, setReturnedData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [returnItems, setReturnItems] = useState([]);
    const [storageLocationOptions, setStorageLocationOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [productTypeOptions, setProductTypeOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subCategoryOptions, setSubCategoryOptions] = useState([]);

    const [activeTab, setActiveTab] = useState("active");

    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);

    // ── View modal state: header fields + Product tab (line items for
    //    one return_id) + Docs tab (printable slip via GoodsReturnForm) ──
    const [docsModal, setDocsModal] = useState(null); // { returnNo, items, activeDetailTab, autoPrint?, autoDownload?, previewOpen? }
    const [docsModalLoading, setDocsModalLoading] = useState(false);

    useEffect(() => {
        if (docsModal?.autoPrint) {
            const timer = setTimeout(() => printGoodsReturn(), 1200);
            return () => clearTimeout(timer);
        }
        if (docsModal?.autoDownload) {
            const timer = setTimeout(() => {
                downloadGoodsReturn(`${docsModal.returnNo}.pdf`);
            }, 1200);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docsModal]);

    // ── Opens the View modal for a return_id: fetches every line item
    //    recorded against that return and shows the Product tab by default
    //    (the Docs tab renders the printable Goods Return slip).
    const openDetailModal = async (returnId) => {
        try {
            setDocsModalLoading(true);

            const res = await axios.get(
                `${PURCHASE_API}/goods-return/slip/${returnId}`
            );

            setDocsModal({
                returnNo: returnId,
                items: res.data.data || [],
                activeDetailTab: "product",
            });
        } catch (err) {
            console.error("Failed to load return details:", err);
            toast.error("Failed to load return details");
        } finally {
            setDocsModalLoading(false);
        }
    };

    const downloadGoodsReturn = async (filename) => {
        const printArea = document.getElementById("gr-print-area");
        if (!printArea) return;
        try {
            const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
                import("html2canvas"),
                import("jspdf"),
            ]);
            const canvas = await html2canvas(printArea, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let yOffset = 10;
            let remaining = imgHeight;
            while (remaining > 0) {
                pdf.addImage(imgData, "PNG", 10, yOffset, imgWidth, Math.min(remaining, pageHeight - 20));
                remaining -= (pageHeight - 20);
                if (remaining > 0) { pdf.addPage(); yOffset = 10; }
            }
            pdf.save(filename || "GoodsReturn.pdf");
        } catch (err) {
            console.error("PDF download failed:", err);
            toast.error("PDF download failed. Please try Print instead.");
        }
    };

    const printGoodsReturn = () => {
        const printArea = document.getElementById("gr-print-area");
        if (!printArea) return;
        const styleSheets = Array.from(document.styleSheets)
            .map((ss) => { try { return Array.from(ss.cssRules || []).map((r) => r.cssText).join("\n"); } catch { return ""; } })
            .join("\n");
        const iframe = document.createElement("iframe");
        iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;";
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Goods Return</title>
<style>${styleSheets}
body{background:#fff!important;margin:0;padding:16px;font-family:Arial,Helvetica,sans-serif;}
@page{size:A4;margin:14mm;}
</style></head><body>${printArea.outerHTML}</body></html>`);
        doc.close();
        iframe.contentWindow.focus();
        setTimeout(() => {
            iframe.contentWindow.print();
            setTimeout(() => document.body.removeChild(iframe), 1000);
        }, 400);
    };

    // Batch Stock Modal states
    const [batchModalData, setBatchModalData] = useState(null);
    const [rowAvailableStocks, setRowAvailableStocks] = useState({});

    // ── Filter Matrix State
    const [filters, setFilters] = useState({
        productType: null,
        category: null,
        subCategory: null,
        storageLocation: null,
        productCode: null,
        fromDate: "",
        toDate: "",
    });

    // ── Unique composite key generator for stable row checking
    const getRowKey = (item, index) =>
        `${item.id || index}||${item.batch_number || ""}`;

    // ── Fetch metadata dropdown items on initialization
    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                const [productTypes, categories, subCategories, storageLocs, products] = await Promise.all([
                    axios.get(`${MASTER_API}/configuration/product-types`),
                    axios.get(`${MASTER_API}/configuration/category`),
                    axios.get(`${MASTER_API}/configuration/sub-category`),
                    axios.get(`${PURCHASE_API}/goods-request/storage-locations`),
                    axios.get(`${PURCHASE_API}/goods-request/product-options`)
                ]);

                setProductTypeOptions((productTypes.data || []).map(r => ({ value: r.name || r.id, label: r.name })));
                setCategoryOptions((categories.data || []).map(r => ({ value: r.name || r.id, label: r.name })));
                setSubCategoryOptions((subCategories.data || []).map(r => ({ value: r.name || r.id, label: r.name })));
                setStorageLocationOptions((storageLocs.data.data || []).map(r => ({
                    value: r.storage_code,
                    label: `${r.storage_code} - ${r.storage_location}`,
                    storage_location: r.storage_location
                })));
                setProductOptions((products.data.data || []).map(r => ({
                    value: r.productCode,
                    label: `${r.productCode} - ${r.productDescription}`
                })));
            } catch (err) {
                console.error("Error loading layout metadata fields:", err);
            }
        };
        fetchDropdowns();
    }, []);

    // ── Fetch Grid Data for Active tab, Returned Items list for Returned tab
    const fetchMainDataList = async () => {
        if (activeTab === "returned") {
            try {
                const params = {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate
                };
                const res = await axios.get(`${PURCHASE_API}/goods-return/returned-items`, { params });
                setReturnedData(res.data.data || []);
            } catch (err) {
                console.error("Failed loading returned items:", err);
                toast.error("Error loading returned items.");
            }
            return;
        }

        if (activeTab !== "active") {
            setData([]);
            return;
        }
        try {
            const params = {
                fromDate: filters.fromDate,
                toDate: filters.toDate
            };
            const res = await axios.get(`${PURCHASE_API}/goods-return/active-items`, { params });
            const list = res.data.data || [];
            setData(list);
            setSelectedRows([]);

            // Trigger stock lookups for live metrics mapping counters
            list.forEach((item, index) => {
                loadRowAvailableStock(item.product_code, item.to_storage_code, getRowKey(item, index));
            });
        } catch (err) {
            console.error("Failed syncing tracking records:", err);
            toast.error("Error connecting to inventory databases.");
        }
    };

    const loadRowAvailableStock = async (productCode, storageCode, compositeKey) => {
        if (!productCode || !storageCode) return;
        try {
            const res = await axios.get(`${PURCHASE_API}/goods-request/available-stock`, {
                params: { productCode, storageCode }
            });
            setRowAvailableStocks(prev => ({ ...prev, [compositeKey]: res.data.data || [] }));
        } catch (err) {
            console.error("Failed background batch compilation:", err);
        }
    };

    useEffect(() => {
        fetchMainDataList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filters.fromDate, filters.toDate]);

    // ── Open batch breakdown overlay modal
    const openStockModal = (productCode, description, storageLocationLabel, stocks) => {
        setBatchModalData({
            product_code: productCode,
            description: description,
            storage_label: storageLocationLabel,
            batches: stocks || []
        });
    };

    const handleSelect = (item, index) => {
        const key = getRowKey(item, index);
        const stockRows = rowAvailableStocks[key] || [];
        if (sumQty(stockRows) <= 0) {
            toast.warning(`No available stock to return for ${item.product_code} at this location.`);
            return;
        }
        if (selectedRows.some(x => getRowKey(x.item, x.index) === key)) {
            setSelectedRows(selectedRows.filter(x => getRowKey(x.item, x.index) !== key));
        } else {
            setSelectedRows([...selectedRows, { item, index }]);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const selectable = filteredData
                .map((item, index) => ({ item, index }))
                .filter(({ item, index }) => sumQty(rowAvailableStocks[getRowKey(item, index)] || []) > 0);
            setSelectedRows(selectable);
        } else {
            setSelectedRows([]);
        }
    };

    const openReturnModal = () => {
        if (selectedRows.length === 0) {
            toast.warning("Please select at least one item to return");
            return;
        }
        const today = dayjs().format("YYYY-MM-DD");
        setReturnItems(selectedRows.map(({ item, index }) => {
            const compKey = getRowKey(item, index);
            const stockRows = rowAvailableStocks[compKey] || [];
            return {
                ...item,
                return_qty: "",
                return_date: today,
                to_storage_code: item.to_storage_code || "",
                to_storage_location: item.to_storage_location || "",
                stockRows: stockRows,
                totalStockCount: sumQty(stockRows)
            };
        }));
        setShowModal(true);
    };

    const handleSaveReturn = async () => {
        for (const item of returnItems) {
            if (!item.return_qty || Number(item.return_qty) <= 0 || Number(item.return_qty) > Number(item.issued_qty || item.qty)) {
                toast.warning(`Please enter a valid Return Qty for ${item.product_code}`);
                return;
            }
            if (Number(item.totalStockCount || 0) <= 0) {
                toast.warning(`${item.product_code} has no available stock at this location and cannot be returned.`);
                return;
            }
            if (Number(item.return_qty) > Number(item.totalStockCount)) {
                toast.warning(`Return Qty for ${item.product_code} cannot exceed available stock (${formatQty(item.totalStockCount)}).`);
                return;
            }
            if (!item.return_date) {
                toast.warning(`Please enter a valid Return Date for ${item.product_code}`);
                return;
            }
        }

        try {
            const loggedInUserId = getCurrentUserId();
            const itemsWithUser = returnItems.map(item => ({ ...item, returned_by: loggedInUserId }));
            const res = await axios.post(`${PURCHASE_API}/goods-return/save`, itemsWithUser);
            if (res.data.success) {
                toast.success(`Success! Generated ID: ${res.data.return_id}`);
                setShowModal(false);
                fetchMainDataList();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Transactional Save Failed.");
        }
    };

    // ── Filters Parsing Logic Matrix
    const filteredData = useMemo(() => {
        if (activeTab !== "active") return [];
        const base = (data || []).filter((item) => {
            if (filters.productCode && item.product_code !== filters.productCode.value) return false;
            if (filters.productType && item.product_type_name !== filters.productType.value) return false;
            if (filters.category && item.category_name !== filters.category.value) return false;
            if (filters.subCategory && item.sub_category_name !== filters.subCategory.value) return false;
            if (filters.storageLocation && item.to_storage_location !== filters.storageLocation.label) return false;
            return true;
        });
        // Hide rows where the resolved available stock at the destination
        // location is zero — nothing to return if there's no stock there.
        return base.filter((item, index) => {
            const key = getRowKey(item, index);
            const stockRows = rowAvailableStocks[key];
            if (stockRows === undefined) return true; // stock still loading, don't hide yet
            return sumQty(stockRows) > 0;
        });
    }, [data, filters, activeTab, rowAvailableStocks]);

    // UI Inline CSS Layout Structuring Configs
    const styles = {
        // Added flex layout alignments here to perfectly center the modal box
        modalBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050 },
        modal: { background: "#fff", borderRadius: "8px", maxHeight: "85vh", width: "80%", maxWidth: "1100px", display: "flex", flexDirection: "column", overflow: "hidden" },
        modalHeader: { padding: "12px 20px", borderBottom: "1px solid #ddd", display: "flex", justifyContent: "space-between", alignItems: "center" },
        modalBody: { padding: "20px", overflow: "auto" },
        modalFooter: { padding: "12px 20px", borderTop: "1px solid #ddd", display: "flex", justifyContent: "flex-end", gap: "10px" },
        filterCard: { background: "#fff", borderRadius: "8px", padding: "15px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: "15px" },
        compactLabel: { fontSize: "12px", fontWeight: "600", color: "#334155", marginBottom: "4px", display: "block" },
        tableContainer: { background: "#fff", borderRadius: 8, padding: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflowX: "auto" },
        erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: 10, borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
        erpTd: { fontSize: 13, padding: 10, borderBottom: "1px solid #f1f5f9", color: "#334155" },
        iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "#64748b", padding: "0 4px" },
        cellLink: { color: "#2563eb", textDecoration: "underline", fontWeight: 600, cursor: "pointer" },
        selectBox: { control: (p) => ({ ...p, minHeight: "38px", fontSize: "13px" }) },
        readOnlyBox: {
            fontSize: "13px",
            color: "#1e293b",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            padding: "6px 10px",
            minHeight: "32px",
            display: "flex",
            alignItems: "center",
        },
    };

    return (
        <Layout>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 }}>
                <div>
                    <h2 style={{ fontSize: "22px", fontWeight: "700", color: "#1e293b" }}>Goods Return</h2>
                    <p style={{ fontSize: "13px", color: "#64748b" }}>Raw Material Returns Processing</p>
                </div>
                {/* --- User Manual Icon Button --- */}
                <button
                    onClick={() => {
                        setManualExists(null);
                        setManualFile(null);
                        setShowManual(true);
                        fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                            .then((res) => res.json())
                            .then((data) => {
                                const norm = (v) => String(v || "").trim().toLowerCase();
                                const match = (data?.data || []).find((m) =>
                                    norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                    norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                    norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                );
                                if (match && match.drive_link) {
                                    setManualFile(match);
                                    setManualExists(true);
                                } else {
                                    setManualExists(false);
                                }
                            })
                            .catch(() => setManualExists(false));
                    }}
                    title="View User Manual"
                    style={{
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        padding: "10px 14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        color: "#3b82f6",
                        fontSize: "14px",
                        fontWeight: "600",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                >
                    <FileText size={18} />
                    User Manual
                </button>
            </div>

            {/* --- User Manual Popup Modal --- */}
            {showManual && (
                <div
                    onClick={() => setShowManual(false)}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(15, 23, 42, 0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
                        {/* Modal Header */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "14px 20px",
                                borderBottom: "1px solid #e2e8f0",
                            }}
                        >
                            <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                User Manual
                            </h4>
                            <button
                                onClick={() => setShowManual(false)}
                                style={{
                                    border: "none",
                                    background: "#f1f5f9",
                                    borderRadius: "8px",
                                    width: "32px",
                                    height: "32px",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    color: "#475569",
                                    lineHeight: 1,
                                }}
                                title="Close"
                            >
                                ×
                            </button>
                        </div>

                        {/* PDF Viewer / Fallback */}
                        {manualExists === null && (
                            <div style={{
                                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#94a3b8", fontSize: "14px"
                            }}>
                                Checking for user manual...
                            </div>
                        )}

                        {manualExists === false && (
                            <div style={{
                                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                            }}>
                                <FileText size={40} color="#cbd5e1" />
                                <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                    User Manual Not Available
                                </div>
                                <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                </div>
                            </div>
                        )}

                        {manualExists === true && manualFile && (() => {
                            const MANUAL_SCALE = 1.25; // 1.0 = normal, 1.25 = 25% bigger, 1.5 = 50% bigger
                            return (
                                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                    <iframe
                                        src={manualFile.drive_link.replace("/view", "/preview")}
                                        title={manualFile.file_name || "User Manual"}
                                        allow="autoplay"
                                        style={{
                                            border: "none",
                                            width: `${100 / MANUAL_SCALE}%`,
                                            height: `${100 / MANUAL_SCALE}%`,
                                            transform: `scale(${MANUAL_SCALE})`,
                                            transformOrigin: "top left",
                                        }}
                                    />
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* ── Filter Matrix Rows ── */}
            <div style={styles.filterCard}>
                <div className="row g-3">
                    <div className="col-md-3">
                        <label style={styles.compactLabel}>Product Type</label>
                        <Select styles={styles.selectBox} options={productTypeOptions} value={filters.productType} onChange={v => setFilters(p => ({ ...p, productType: v }))} isClearable />
                    </div>
                </div>

                <div className="row g-3 mt-1">
                    <div className="col-md-4">
                        <label style={styles.compactLabel}>Category</label>
                        <Select styles={styles.selectBox} options={categoryOptions} value={filters.category} onChange={v => setFilters(p => ({ ...p, category: v }))} isClearable />
                    </div>
                    <div className="col-md-4">
                        <label style={styles.compactLabel}>Sub Category</label>
                        <Select styles={styles.selectBox} options={subCategoryOptions} value={filters.subCategory} onChange={v => setFilters(p => ({ ...p, subCategory: v }))} isClearable />
                    </div>
                    <div className="col-md-4">
                        <label style={styles.compactLabel}>Storage Location (To)</label>
                        <Select styles={styles.selectBox} options={storageLocationOptions} value={filters.storageLocation} onChange={v => setFilters(p => ({ ...p, storageLocation: v }))} isClearable />
                    </div>
                </div>

                <div className="row g-3 mt-1">
                    <div className="col-md-4">
                        <label style={styles.compactLabel}>Product Code</label>
                        <Select styles={styles.selectBox} options={productOptions} value={filters.productCode} onChange={v => setFilters(p => ({ ...p, productCode: v }))} isClearable />
                    </div>
                    <div className="col-md-4">
                        <label style={styles.compactLabel}>From Date</label>
                        <DateInputDMY value={filters.fromDate} onChange={v => setFilters(p => ({ ...p, fromDate: v }))} />
                    </div>
                    <div className="col-md-4">
                        <label style={styles.compactLabel}>To Date</label>
                        <DateInputDMY value={filters.toDate} onChange={v => setFilters(p => ({ ...p, toDate: v }))} />
                    </div>
                </div>
            </div>

            {/* ── Action Toggles ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                <div className="gr-tab-toggle">
                    <button className={activeTab === "active" ? "active-tab" : ""} onClick={() => setActiveTab("active")}>Active</button>
                    <button className={activeTab === "returned" ? "active-tab" : ""} onClick={() => setActiveTab("returned")}>Returned</button>
                </div>
                {activeTab === "active" && <button className="btn btn-dark" onClick={openReturnModal}>Return</button>}
            </div>

            {/* ── Table Container Workspaces ── */}
            <div style={styles.tableContainer}>
                {activeTab === "active" ? (
                    <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={styles.erpTh}>
                                    <input type="checkbox" checked={filteredData.length > 0 && selectedRows.length === filteredData.length} onChange={handleSelectAll} />
                                </th>
                                <th style={styles.erpTh}>Batch No</th>
                                <th style={styles.erpTh}>Product Code</th>
                                <th style={styles.erpTh}>Description</th>
                                <th style={styles.erpTh}>UOM</th>
                                <th style={styles.erpTh}>Qty</th>
                                <th style={styles.erpTh}>Issue Date</th>
                                <th style={styles.erpTh}>Available Stock</th>
                                <th style={styles.erpTh}>Storage Location</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((item, index) => {
                                const compKey = getRowKey(item, index);
                                const isChecked = selectedRows.some(x => getRowKey(x.item, x.index) === compKey);
                                const stockRows = rowAvailableStocks[compKey] || [];
                                const totalStockCount = sumQty(stockRows);

                                return (
                                    <tr key={index}>
                                        <td style={styles.erpTd}>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => handleSelect(item, index)}
                                            />
                                        </td>
                                        <td style={styles.erpTd}>{item.batch_number || "-"}</td>
                                        <td style={styles.erpTd}>
                                            <Link
                                                to={`/cable/product/product-code?view=${encodeURIComponent(
                                                    item.product_code
                                                )}&returnTo=${encodeURIComponent(
                                                    location.pathname + location.search
                                                )}`}
                                                style={styles.cellLink}
                                            >
                                                {item.product_code}
                                            </Link>
                                        </td>
                                        <td style={styles.erpTd}>{item.description}</td>
                                        <td style={styles.erpTd}>{item.uom}</td>
                                        <td style={styles.erpTd}>{formatQty(item.issued_qty)}</td>
                                        <td style={styles.erpTd}>{item.issue_date ? dayjs(item.issue_date).format("YYYY-MM-DD") : "-"}</td>
                                        <td style={styles.erpTd}>{formatQty(totalStockCount) || "0"}</td>
                                        <td style={styles.erpTd}>
                                            <span style={styles.cellLink} onClick={() => openStockModal(item.product_code, item.description, item.to_storage_location, stockRows)}>
                                                {item.to_storage_location || "-"}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={styles.erpTh}>Return ID</th>
                                <th style={styles.erpTh}>Return Date</th>
                                <th style={styles.erpTh}>Batch No</th>
                                <th style={styles.erpTh}>Product Code</th>
                                <th style={styles.erpTh}>Description</th>
                                <th style={styles.erpTh}>UOM</th>
                                <th style={styles.erpTh}>Qty</th>
                                <th style={styles.erpTh}>From Location</th>
                                <th style={styles.erpTh}>To Location</th>
                                <th style={styles.erpTh}>Status</th>
                                <th style={styles.erpTh}>Docs</th>
                            </tr>
                        </thead>
                        <tbody>
                            {returnedData.length > 0 ? (
                                returnedData.map((item, idx) => (
                                    <tr key={idx}>
                                        <td style={styles.erpTd}>{item.return_id}</td>
                                        <td style={styles.erpTd}>{item.return_date ? dayjs(item.return_date).format("YYYY-MM-DD") : "-"}</td>
                                        <td style={styles.erpTd}>{item.batch_number || "-"}</td>
                                        <td style={styles.erpTd}>
                                            <Link
                                                to={`/cable/product/product-code?view=${encodeURIComponent(
                                                    item.product_code
                                                )}&returnTo=${encodeURIComponent(
                                                    location.pathname + location.search
                                                )}`}
                                                style={styles.cellLink}
                                            >
                                                {item.product_code}
                                            </Link>
                                        </td>
                                        <td style={styles.erpTd}>{item.description}</td>
                                        <td style={styles.erpTd}>{item.uom}</td>
                                        <td style={styles.erpTd}>{formatQty(item.qty)}</td>
                                        <td style={styles.erpTd}>{item.from_storage_location || "-"}</td>
                                        <td style={styles.erpTd}>{item.to_storage_location || "-"}</td>
                                        <td style={styles.erpTd}>{item.status}</td>
                                        <td style={styles.erpTd}>
                                            {item.return_id ? (
                                                <button
                                                    className="btn btn-sm btn-dark"
                                                    style={{ fontSize: "11px", padding: "2px 8px" }}
                                                    onClick={() => openDetailModal(item.return_id)}
                                                >
                                                    View
                                                </button>
                                            ) : "-"}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={11} style={{ ...styles.erpTd, textAlign: "center", color: "#64748b" }}>
                                        No returned items found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Centered Goods Return Dialog Modal ── */}
            {showModal && (
                <div style={styles.modalBackdrop}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>Goods Return</h3>
                            <button className="btn-close" onClick={() => setShowModal(false)}></button>
                        </div>
                        <div style={{ padding: "8px 20px 0", fontSize: 13, color: "#475569" }}>
                            Returned By: <strong>{getCurrentUserId() || "-"}</strong>
                        </div>
                        <div style={styles.modalBody}>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th style={styles.erpTh}>Batch No</th>
                                        <th style={styles.erpTh}>Product Code</th>
                                        <th style={styles.erpTh}>Description</th>
                                        <th style={styles.erpTh}>UOM</th>
                                        <th style={styles.erpTh}>Qty</th>
                                        <th style={styles.erpTh}>Available Stock</th>
                                        <th style={styles.erpTh}>Storage Location</th>
                                        <th style={styles.erpTh}>Return Qty</th>
                                        <th style={styles.erpTh}>Return Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {returnItems.map((item, idx) => (
                                        <tr key={idx}>
                                            <td style={styles.erpTd}>{item.batch_number || "-"}</td>
                                            <td style={styles.erpTd}>
                                                <Link
                                                    to={`/cable/product/product-code?view=${encodeURIComponent(
                                                        item.product_code
                                                    )}&returnTo=${encodeURIComponent(
                                                        location.pathname + location.search
                                                    )}`}
                                                    style={styles.cellLink}
                                                >
                                                    {item.product_code}
                                                </Link>
                                            </td>
                                            <td style={styles.erpTd}>{item.description}</td>
                                            <td style={styles.erpTd}>{item.uom}</td>
                                            <td style={styles.erpTd}>{formatQty(item.issued_qty)}</td>
                                            <td style={styles.erpTd}>{formatQty(item.totalStockCount) || "0"}</td>
                                            <td style={styles.erpTd}>
                                                <span
                                                    style={styles.cellLink}
                                                    onClick={() => openStockModal(item.product_code, item.description, item.to_storage_location, item.stockRows)}
                                                >
                                                    {item.to_storage_location || "-"}
                                                </span>
                                            </td>
                                            <td style={styles.erpTd}>
                                                <input type="number" className="form-control form-control-sm" style={{ width: "95px" }} max={item.totalStockCount || 0} min={0} value={item.return_qty} onChange={e => {
                                                    const copy = [...returnItems];
                                                    copy[idx].return_qty = e.target.value;
                                                    setReturnItems(copy);
                                                }} />
                                            </td>
                                            <td style={styles.erpTd}>
                                                <DateInputDMY
                                                    value={item.return_date}
                                                    onChange={v => {
                                                        const copy = [...returnItems];
                                                        copy[idx].return_date = v;
                                                        setReturnItems(copy);
                                                    }}
                                                    style={{ width: "150px" }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={styles.modalFooter}>
                            <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                            <button className="btn btn-dark" onClick={handleSaveReturn}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Batch Detail Modal Layout ── */}
            {batchModalData && (
                <div
                    style={{ ...styles.modalBackdrop, zIndex: 1100 }}
                    onClick={() => setBatchModalData(null)}
                >
                    <div
                        style={{ ...styles.modal, maxWidth: "480px" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={styles.modalHeader}>
                            <div>
                                <p
                                    style={{
                                        margin: 0,
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: "#1e293b",
                                    }}
                                >
                                    {batchModalData.product_code} - {batchModalData.description}
                                </p>
                                {batchModalData.storage_label && (
                                    <p
                                        style={{
                                            margin: "2px 0 0",
                                            fontSize: "12px",
                                            color: "#64748b",
                                        }}
                                    >
                                        {batchModalData.storage_label}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => setBatchModalData(null)}
                                style={styles.iconBtn}
                            >
                                ✖
                            </button>
                        </div>

                        <div style={styles.modalBody}>
                            {batchModalData.batches.length > 0 ? (
                                <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Batch No</th>
                                            <th style={styles.erpTh}>Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batchModalData.batches.map((b, i) => (
                                            <tr key={i}>
                                                <td style={styles.erpTd}>{b.batch_number || "-"}</td>
                                                <td style={styles.erpTd}>{formatQty(b.issued_qty)}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td style={{ ...styles.erpTd, fontWeight: 700 }}>Total</td>
                                            <td style={{ ...styles.erpTd, fontWeight: 700 }}>
                                                {formatQty(sumQty(batchModalData.batches))}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                                    No stock available.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* ── Goods Return View Modal (header + Product/Docs tabs) ── */}
            {docsModal && (
                <div
                    style={{ ...styles.modalBackdrop, zIndex: 1200 }}
                    onClick={() => setDocsModal(null)}
                >
                    <div
                        style={{ ...styles.modal, maxWidth: "1100px", width: "95%", height: "90vh" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={styles.modalHeader}>
                            <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                                Return : {docsModal.returnNo}
                            </p>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <button onClick={() => setDocsModal(null)} style={styles.iconBtn}>✖</button>
                            </div>
                        </div>

                        <div style={{ padding: "16px 20px 0" }}>
                            {/* ── Header fields (fixed, same across tabs) ─────── */}
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(4, 1fr)",
                                    gap: "16px",
                                    marginBottom: "16px",
                                }}
                            >
                                <div>
                                    <label style={styles.compactLabel}>Return ID</label>
                                    <div style={styles.readOnlyBox}>{docsModal.returnNo}</div>
                                </div>
                                <div>
                                    <label style={styles.compactLabel}>Return Date</label>
                                    <div style={styles.readOnlyBox}>
                                        {docsModal.items?.[0]?.return_date
                                            ? dayjs(docsModal.items[0].return_date).format("YYYY-MM-DD")
                                            : "-"}
                                    </div>
                                </div>
                                <div>
                                    <label style={styles.compactLabel}>Status</label>
                                    <div style={styles.readOnlyBox}>
                                        {docsModal.items?.[0]?.status || "-"}
                                    </div>
                                </div>
                                <div>
                                    <label style={styles.compactLabel}>Returned By</label>
                                    <div style={styles.readOnlyBox}>
                                        {docsModal.items?.[0]?.returned_by || "-"}
                                    </div>
                                </div>
                            </div>

                            {/* ── Tab toggle: Product / Docs ──────────────────── */}
                            <div className="gr-tab-toggle" style={{ marginBottom: "12px" }}>
                                <button
                                    className={docsModal.activeDetailTab === "product" ? "active-tab" : ""}
                                    onClick={() =>
                                        setDocsModal((prev) => ({ ...prev, activeDetailTab: "product" }))
                                    }
                                >
                                    Product
                                </button>
                                <button
                                    className={docsModal.activeDetailTab === "docs" ? "active-tab" : ""}
                                    onClick={() =>
                                        setDocsModal((prev) => ({ ...prev, activeDetailTab: "docs" }))
                                    }
                                >
                                    Docs
                                </button>
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 20px" }}>
                            {/* ── Product tab: line-item table ────────────────── */}
                            {docsModal.activeDetailTab === "product" && (
                                docsModalLoading ? (
                                    <p style={{ fontSize: 13, color: "#64748b" }}>Loading...</p>
                                ) : (
                                    <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th style={styles.erpTh}>Batch No</th>
                                                <th style={styles.erpTh}>Product Code</th>
                                                <th style={styles.erpTh}>Description</th>
                                                <th style={styles.erpTh}>UOM</th>
                                                <th style={styles.erpTh}>Qty</th>
                                                <th style={styles.erpTh}>From Storage Location</th>
                                                <th style={styles.erpTh}>To Storage Location</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(docsModal.items || []).map((item, idx) => (
                                                <tr key={idx}>
                                                    <td style={styles.erpTd}>{item.batch_number || "-"}</td>
                                                    <td style={styles.erpTd}>{item.product_code}</td>
                                                    <td style={styles.erpTd}>{item.description}</td>
                                                    <td style={styles.erpTd}>{item.uom}</td>
                                                    <td style={styles.erpTd}>{formatQty(item.qty)}</td>
                                                    <td style={styles.erpTd}>{item.from_storage_location || "-"}</td>
                                                    <td style={styles.erpTd}>{item.to_storage_location || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}

                            {/* ── Docs tab: file upload row + document card ──── */}
                            {docsModal.activeDetailTab === "docs" && (
                                <div>
                                    <div style={{ display: "flex", gap: "0", marginBottom: "6px" }}>
                                        <label
                                            style={{
                                                fontSize: "13px",
                                                padding: "6px 14px",
                                                background: "#e2e8f0",
                                                border: "1px solid #cbd5e1",
                                                borderRight: "none",
                                                borderRadius: "6px 0 0 6px",
                                                cursor: "pointer",
                                                color: "#1e293b",
                                            }}
                                        >
                                            Choose Files
                                            <input
                                                type="file"
                                                style={{ display: "none" }}
                                                onChange={(e) =>
                                                    setDocsModal((prev) => ({
                                                        ...prev,
                                                        chosenFileName: e.target.files?.[0]?.name || null,
                                                    }))
                                                }
                                            />
                                        </label>
                                        <div
                                            style={{
                                                fontSize: "13px",
                                                padding: "6px 14px",
                                                border: "1px solid #cbd5e1",
                                                borderRadius: "0 6px 6px 0",
                                                color: "#64748b",
                                                flex: 1,
                                            }}
                                        >
                                            {docsModal.chosenFileName || "No file chosen"}
                                        </div>
                                    </div>
                                    <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "16px" }}>
                                        Supported formats: PDF, DOCX, XLSX
                                    </p>

                                    <div
                                        style={{
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "8px",
                                            padding: "14px 16px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: "#1e293b",
                                                marginBottom: "10px",
                                            }}
                                        >
                                            <span>📄</span>
                                            <span>{docsModal.returnNo}_GoodsReturn.pdf</span>
                                        </div>
                                        <div style={{ display: "flex", gap: "8px" }}>
                                            <button
                                                className="btn btn-sm btn-dark"
                                                onClick={() =>
                                                    setDocsModal((prev) => ({
                                                        ...prev,
                                                        previewOpen: true,
                                                        autoPrint: false,
                                                        autoDownload: false,
                                                    }))
                                                }
                                            >
                                                View
                                            </button>
                                            <button
                                                className="btn btn-sm btn-dark"
                                                onClick={() =>
                                                    setDocsModal((prev) => ({
                                                        ...prev,
                                                        previewOpen: true,
                                                        autoDownload: true,
                                                        autoPrint: false,
                                                    }))
                                                }
                                            >
                                                Download
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() =>
                                                    setDocsModal((prev) => ({
                                                        ...prev,
                                                        previewOpen: true,
                                                        autoPrint: true,
                                                        autoDownload: false,
                                                    }))
                                                }
                                            >
                                                Print
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Full Slip Preview Modal (opened from the Docs tab's
                    document card) ─────────────────────────────────── */}
            {docsModal?.previewOpen && (
                <div
                    style={{ ...styles.modalBackdrop, zIndex: 1300 }}
                    onClick={() =>
                        setDocsModal((prev) => ({ ...prev, previewOpen: false }))
                    }
                >
                    <div
                        style={{ ...styles.modal, maxWidth: "900px", width: "95%", height: "90vh" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={styles.modalHeader}>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#1e293b",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}
                            >
                                <span>📄</span>
                                <span>{docsModal.returnNo}_GoodsReturn.pdf</span>
                            </p>
                            <button
                                className="btn btn-sm"
                                style={{
                                    fontSize: "12px",
                                    color: "#dc2626",
                                    border: "1px solid #dc2626",
                                    background: "#fff",
                                    borderRadius: "6px",
                                    padding: "4px 12px",
                                }}
                                onClick={() =>
                                    setDocsModal((prev) => ({ ...prev, previewOpen: false }))
                                }
                            >
                                ✖ Close
                            </button>
                        </div>

                        <div style={{ position: "relative", flex: 1, overflowY: "auto" }}>
                            <button
                                className="btn btn-sm btn-dark"
                                style={{
                                    position: "absolute",
                                    top: "12px",
                                    right: "20px",
                                    fontSize: "12px",
                                    zIndex: 2,
                                }}
                                onClick={() => downloadGoodsReturn(`${docsModal.returnNo}.pdf`)}
                            >
                                Download PDF
                            </button>
                            <div style={{ padding: "16px" }}>
                                <GoodsReturnForm returnNo={docsModal.returnNo} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} />
            <style>{`
                .gr-tab-toggle { display: inline-flex; background: #f1f5f9; border-radius: 8px; padding: 3px; gap: 2px; }
                .gr-tab-toggle button { border: none; background: transparent; padding: 6px 18px; font-size: 13px; font-weight: 600; border-radius: 6px; color: #64748b; cursor: pointer; }
                .gr-tab-toggle button.active-tab { background: #fff; color: #1e293b; box-shadow: 0 1px 3px rgba(0,0,0,.15); }
            `}</style>
        </Layout>
    );
}
export default GoodsReturn;